import { EditorChangeType } from '../types';
import { KaraokeTrackManager } from '../lib/KaraokeTrackManager';
import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

export class RemoveElementCommand implements Command {
  private editor: VideoEditor;

  getName(): string {
    return 'RemoveElementCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }

  private elementId: string;
  private removedElement?: Element;
  private trackId?: string;
  private elementRemoved: boolean = false;

  constructor(editor: VideoEditor, elementId: string) {
    this.editor = editor;
    this.elementId = elementId;
    this.removedElement = this.editor.getElementById(elementId);
    this.trackId = this.editor.timeline.getTrackIdForElement(elementId);
  }

  execute(): boolean {
    if (!this.removedElement || !this.trackId) return false;

    const isTranscriptElement = this.removedElement instanceof VideoTranscriptElement;
    const success = this.editor.timeline.removeElement(this.elementId);
    if (success) {
      this.elementRemoved = true;
      this.editor.markDirty('remove_element_command');
      this.editor.notifyObservers(EditorChangeType.ELEMENT_REMOVED, {
        elementId: this.elementId,
        element: this.removedElement,
      });

      // Reconcile karaoke track if transcript element was removed
      if (isTranscriptElement) {
        const karaokeTrack = this.editor.getKaraokeTrack();
        if (karaokeTrack) {
          const karaokeTrackManager = new KaraokeTrackManager(this.editor);
          karaokeTrackManager.reconcileKaraokeTrack();
        }
      }
    }
    return success;
  }

  undo(): boolean {
    if (!this.elementRemoved || !this.removedElement || !this.trackId)
      return false;

    const success = this.editor.timeline.addElement(
      this.trackId,
      this.removedElement,
    );
    if (success) {
      this.elementRemoved = false;
      this.editor.markDirty('remove_element_command_undo');
      this.editor.notifyObservers(EditorChangeType.ELEMENT_ADDED, {
        element: this.removedElement,
        trackId: this.trackId,
      });
    }
    return success;
  }

  redo(): boolean {
    if (this.elementRemoved) return false;
    return this.execute();
  }

  getDescription(): string {
    const description = this.removedElement
      ? `${this.removedElement.type} element`
      : `element ${this.elementId}`;
    return `Remove ${description}`;
  }
}
